/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { messages } from '../i18n';
import { type ProductSelectOption } from '../../types';
import {
	getProductTitle,
	JiraWorkManagementProduct,
	JiraSoftwareProduct,
	JiraProduct,
} from '../../utils';
import { useInvitePeopleContext } from '.';

const styles = cssMap({
	wrapper: {
		paddingBlockStart: token('space.300', '24px'),
		paddingBlockEnd: token('space.025', '2px'),
	},
});

type OwnProps = {
	productOptions: ProductSelectOption[] | undefined;
	selectedProducts: string[];
	titleId?: string;
};

const FormTitle = ({ productOptions, selectedProducts, titleId }: OwnProps) => {
	const { jiraSubProduct, productTitle } = useInvitePeopleContext();
	const getProductForTitle = useCallback(() => {
		if (selectedProducts.length === 1) {
			const selectedOption = (productOptions || []).find(
				({ value }) => value === selectedProducts[0],
			);
			if (selectedOption) {
				const isJiraProduct = [JiraWorkManagementProduct.id, JiraSoftwareProduct.id].includes(
					selectedOption.value,
				);
				return isJiraProduct ? JiraProduct.label : selectedOption.label;
			} else {
				return !!jiraSubProduct ? getProductTitle(jiraSubProduct) : productTitle;
			}
		}

		return null;
	}, [productTitle, productOptions, jiraSubProduct, selectedProducts]);

	const selectedProductTitle = getProductForTitle();

	const formattedSelectedProductTitle = (
		<FormattedMessage {...messages.formTitleProduct} values={{ product: selectedProductTitle }} />
	);

	const formattedDefaultTitle = <FormattedMessage {...messages.formTitle} />;

	return (
		<Box xcss={styles.wrapper}>
			<Heading as="h1" id={titleId ?? 'invite-people-title'} size="medium">
				{!!selectedProductTitle ? formattedSelectedProductTitle : formattedDefaultTitle}
			</Heading>
		</Box>
	);
};

export default FormTitle;
